import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLeaveBalance } from "../../contexts/LeaveBalanceContext";
import Modal from "../../components/UI/Modal";
import LeaveBalanceForm from "../../components/Form/LeaveBalanceForm";
// import BulkHolidayForm from "../../components/Form/BulkHolidayForm";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import ReactPaginate from 'react-paginate';
import Drawer from "../../components/UI/Drawer";
import { getToken } from "../../utils/tokenUtils";
import { X, Briefcase, MoreVertical, Building, Calendar, Users, MapPin, Award, Clock, Grid, Search } from 'lucide-react';
import DropdownMenu from "../../components/UI/DropdownMenu";

// Separate component for actions column to avoid hooks rule violation
const ActionsColumn = React.memo(({ row, openMenu, setOpenMenu, openModalForEdit, handleLeaveBalance, handleView }) => {
  const buttonRef = useRef(null);
  
  return (
    <div className="flex items-center justify-center relative">
      <button
        ref={buttonRef}
        onClick={() =>
          setOpenMenu(openMenu === row.id ? null : row.id)
        }
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
      >
        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
      </button>

      {openMenu === row.id && (
        <DropdownMenu
          buttonRef={buttonRef}
          onClose={() => setOpenMenu(null)}
        >
          <button
            onClick={(e) => {
              handleView(row);
              setOpenMenu(null);
            }}
            className="w-full text-left px-3 py-2 text-sm text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
          >
            View
          </button>
          <button
            onClick={(e) => {
              openModalForEdit(row);
              setOpenMenu(null);
            }}
            className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            Edit
          </button>
          <DeleteButton
            style={`text-red-600 hover:text-red-800 px-3 py-2`}
            onDelete={(e) => {
              handleLeaveBalance(row.id);
              setOpenMenu(null);
            }}
          />
        </DropdownMenu>
      )}
    </div>
  );
});

const LeaveBalanceMasters = React.memo(() => {
  const { leaveBalances, fetchLeaveBalances, addLeaveBalance, handleBulkLeaveCreate, updateLeaveBalance, deleteLeaveBalance, pagination, loading } = useLeaveBalance();

  // const [loading, setLoading] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedLeaveBalance, setSelectedLeaveBalance] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLeaveBalance, setEditingLeaveBalance] = useState(null);
  const [message, setMessage] = useState(""); // Success message
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltering, setIsFiltering] = useState(false);
  const token = getToken();
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkMessage, setBulkMessage] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
    
  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingLeaveBalance(null);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  // const openModalForBulkAdd = useCallback(() => {
  //   setShowModal(true);
  // }, []);

  const openModalForEdit = useCallback((leaveBalance) => {
    // console.log("edit leave: ", leaveBalance)
    setEditingLeaveBalance(leaveBalance);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const handleView = useCallback((leaveBalance) => {
    setSelectedLeaveBalance(leaveBalance);
    setIsViewModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingLeaveBalance(null);
    setFormErrors({});
    setMessage("");
  }, []);

  const closeViewModal = useCallback(() => {
    setIsViewModalOpen(false);
    setSelectedLeaveBalance(null);
  }, []);

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  // create and update function
  const handleFormSubmit = useCallback(
    async (leaveBalance) => {
      // console.log("leave balance in handle form submit: ", leaveBalance)

      // setLoading(true);
      try {
        if (editingLeaveBalance) {
          // console.log("leave balance payload", leaveBalance);
        
          // Convert FormData to object
          // console.log(leaveBalance instanceof FormData)
          const leaveBalanceData = leaveBalance instanceof FormData ? convertFormDataToObject(leaveBalance) : leaveBalance;
  
          // console.log("Payload being sent:", { ...editingLeaveBalance, ...leaveBalanceData });
  
          const result = await updateLeaveBalance({ ...editingLeaveBalance, ...leaveBalanceData });
          if (result?.success === true) {
            setNotification({
              message: "Leave balance updated successfully.",
              type: "success",
            });
            closeModal();
          } else {
            if (result?.message?.errors) {
              setFormErrors(result.message.errors);
            } else {
              setNotification({
                message: `Error: ${result?.message?.message || result?.message?.error || "Failed to update leave balance."}`,
                type: "error",
              });
            }
          }
        } else {
          const result = await addLeaveBalance(leaveBalance);
          if (result?.success === true) {
            setNotification({
              message: "Leave balance created successfully.",
              type: "success",
            });
            closeModal();
          } else {
            if (result?.message?.errors) {
              setFormErrors(result.message.errors);
            } else {
              setNotification({
                message: `Error: ${result?.message?.error || result?.message?.message || "Failed to add leave balance."}`,
                type: "error",
              });
            }
          }
        }
      } catch (err) {
        if (err.response?.data?.errors) {
          setFormErrors(err.response.data.errors);
        } else {
          setNotification({
            message: `Error: ${err.response?.data?.message || err.response?.data?.error || err.message || "An unexpected error occurred."}`,
            type: "error",
          });
        }
      } finally {
        // setLoading(false);
      }
    },
    [editingLeaveBalance, addLeaveBalance, updateLeaveBalance, closeModal]
  );

 
  // Memoize holidays names
  const leaveBalanceForTable = useMemo(
    () =>
      leaveBalances.map((leaveBalance) => ({
        ...leaveBalance,
        compulsory_national_holiday_balance: leaveBalance?.compulsory_national_holiday_balance || "-",
        public_holiday_balance: leaveBalance?.public_holiday_balance || "-",
        privileged_leave_balance: leaveBalance?.privileged_leave_balance || "-",
        casual_leave_balance: leaveBalance?.casual_leave_balance || "-",
        total_leave_balance: leaveBalance?.total_leave_balance || "-",
        leave_taken: leaveBalance?.leave_taken || "-",
        leave_balance_remaining: leaveBalance?.leave_balance_remaining || "-",
        carry_forward_balance: leaveBalance?.carry_forward_balance || "-",
        is_on_notice: leaveBalance?.is_on_notice,
    })),
    [leaveBalances]
  );

  const filteredLeaveBalances = useMemo(() => {
    return leaveBalanceForTable.filter((item) => {
      const searchTerm = searchQuery.toLowerCase();
      return item.employee_id.toLowerCase().includes(searchTerm);
    });
  }, [leaveBalanceForTable, searchQuery]);

  // delete function
  const handleLeaveBalance = async (id) => {
    try {
      await deleteLeaveBalance(id);
      // setNotification("leave balance deleted successfully");
      setNotification({
        message: "leave balance deleted successfully",
        type: "success",
      });
    } catch (error) {
      // setNotification("Failed to delete leave balance");
      setNotification({
        message: "Failed to delete leave balance",
        type: "error",
      });
    }
  };


  // bulk create function
  // const handleBulkLeaveBalance = async () => {
  //   try {
  //     const response = await handleBulkLeaveCreate();
  //     console.log("response in bulk leave balance: ", response.success)
  //     if (response?.success === false) {
  //         setNotification({
  //         message: response?.message,
  //         type: "error",
  //       });
  //     }
  //     // setNotification("Bulk leave balance created successfully");
  //     setNotification({
  //       message: "Bulk leave balance created successfully",
  //       type: "success",
  //     });
  //   } catch (error) {
  //     // setNotification("Failed to create bulk leave balance");
  //     setNotification({
  //       message: "Failed to create bulk leave balance",
  //       type: "error",
  //     });
  //   }
  // };

  const handleBulkLeaveBalance = async () => {
    setBulkLoading(true);
    setBulkMessage("");
    try {
      const response = await handleBulkLeaveCreate();
      if (response.success) {
        setNotification({
          message: response.message,
          type: "success",
        });
        setIsBulkModalOpen(false);
      } else {
        setBulkMessage(response.message || "Failed to create bulk leave balance");
      }
    } catch (error) {
      setBulkMessage("Unexpected error occurred while creating bulk leave balance");
    } finally {
      setBulkLoading(false);
    }
  };
  
  // columns for table
  const columns = useMemo(
    () => [
      { label: "Employee ID", field: "employee_id" },
      { label: "Total Leaves", field: "total_leave_balance" },
      { label: "Leaves Taken", field: "leave_taken" },
      { label: "Remaining Leaves", field: "leave_balance_remaining" },
      { label: "Notice Period", field: "is_on_notice", render: (row) => (row.is_on_notice === 1 ? "Yes" : "No"), },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <ActionsColumn
            row={row}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            openModalForEdit={openModalForEdit}
            handleLeaveBalance={handleLeaveBalance}
            handleView={handleView}
          />
        ),
      },
    ],
    [
      openMenu,
      openModalForEdit,
      handleLeaveBalance,
      handleView,
    ]
  );
  
  // Fetch leave balances when the component mounts or when the page changes
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchLeaveBalances(newPage);
    }
  };

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      
      {/* notification compo */}
      {notification && (
          <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      {/* title */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-black dark:text-white">Leave Balance Masters</h2>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        {/* Left End: Search Bar */}
        <div className="relative w-full sm:max-w-xl"> 
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by employee ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-poppins"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Right End: Add Buttons */}
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <button
            onClick={openModalForAdd}
            className="bg-blue-600 font-medium text-white px-4 py-2 rounded-md hover:bg-blue-700 whitespace-nowrap"
          >
            Add Leave Balance
          </button>
          <button
            onClick={() => {
              setBulkMessage("");
              setIsBulkModalOpen(true);
            }}
            className="bg-blue-600 font-medium text-white px-4 py-2 rounded-md hover:bg-blue-700 whitespace-nowrap"
          >
            Add Bulk Leave Balance
          </button>
        </div>
      </div>

      {/* Success Message */}
      {message && (
        <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-md">
            {message}
        </div>
      )}

      {/* Leave Balance Table */}
      <ReusableTable
        data={filteredLeaveBalances}
        columns={columns}
        filterable={false}
        excludeFilterColumns={["actions"]}
        onFilterChange={(filtering) => setIsFiltering(filtering)}
      />

      {/* Pagination */}
      {!isFiltering && leaveBalanceForTable?.length > 0 && (
        // <ReactPaginate
        // breakLabel="..."
        // onPageChange={(event) => handlePageChange(event.selected + 1)}
        // forcePage={currentPage - 1}
        // pageRangeDisplayed={5}
        // pageCount={pagination.totalPages}
        // renderOnZeroPageCount={null}
        // nextLabel="Next >"
        // previousLabel="< Prev"
        // previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800'
        // nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
        // containerClassName="flex items-center justify-center mt-4"
        // pageClassName="mx-1 px-3 py-1 border rounded dark:text-white"
        // activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700"
        // />

        <ReactPaginate
          breakLabel="..."
          nextLabel="Next >"
          onPageChange={(event) => handlePageChange(event.selected + 1)}
          forcePage={currentPage - 1}
          pageRangeDisplayed={5}
          disabledClassName='opacity-50 cursor-not-allowed'
          disabledLinkClassName='pointer-events-none'
          pageCount={pagination.totalPages}
          previousLabel="< Prev"
          previousClassName='dark:text-white border border-gray-300 rounded-md dark:border-gray-600'
          previousLinkClassName='block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'
          nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
          nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
          renderOnZeroPageCount={null}
          containerClassName="flex items-center justify-center mt-4 font-poppins"
          pageClassName="mx-1 border rounded dark:text-white font-poppins"
          pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
          activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
          activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
        />
      )}

      {/* Modal for Adding or Editing */}
      <Modal
          isOpen={isModalOpen}
          onRequestClose={closeModal}
          title={editingLeaveBalance ? "Edit Leave Balance" : "Add Leave Balance"}>

          <LeaveBalanceForm
            // loading={loading}
            closeModal={closeModal}
            onSubmit={handleFormSubmit}
            initialValues={editingLeaveBalance || { user_id: "", 
                                                    compulsory_national_holiday_balance: "",
                                                    public_holiday_balance: "", 
                                                    privileged_leave_balance: "",
                                                    casual_leave_balance: "",
                                                    is_on_notice: "" }}
            errors={formErrors}
          />
      </Modal>
    
      {/* Modal for Viewing Details */}
      <Modal
        isOpen={isViewModalOpen}
        onRequestClose={closeViewModal}
        title="Leave Balance Details"
      >
        {selectedLeaveBalance && (
          <div className="p-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="bg-gray-100 dark:bg-black p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
                <span className="text-gray-500 dark:text-gray-400 font-sans font-semibold">Employee ID</span>
                <div className="text-gray-900 dark:text-white font-medium text-lg">{selectedLeaveBalance.employee_id}</div>
              </div>

              <div className="bg-gray-100 dark:bg-black p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
                <span className="text-gray-500 dark:text-gray-400 font-sans font-semibold">National Holiday Balance</span>
                <div className="text-gray-900 dark:text-white font-medium text-lg">{selectedLeaveBalance.compulsory_national_holiday_balance}</div>
              </div>

              <div className="bg-gray-100 dark:bg-black p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
                <span className="text-gray-500 dark:text-gray-400 font-sans font-semibold">Public Holiday Balance</span>
                <div className="text-gray-900 dark:text-white font-medium text-lg">{selectedLeaveBalance.public_holiday_balance}</div>
              </div>

              <div className="bg-gray-100 dark:bg-black p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
                <span className="text-gray-500 dark:text-gray-400 font-sans font-semibold">Privileged Leave Balance</span>
                <div className="text-gray-900 dark:text-white font-medium text-lg">{selectedLeaveBalance.privileged_leave_balance}</div>
              </div>

              <div className="bg-gray-100 dark:bg-black p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
                <span className="text-gray-500 dark:text-gray-400 font-sans font-semibold">Casual Leave Balance</span>
                <div className="text-gray-900 dark:text-white font-medium text-lg">{selectedLeaveBalance.casual_leave_balance}</div>
              </div>

              <div className="bg-gray-100 dark:bg-black p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
                <span className="text-gray-500 dark:text-gray-400 font-sans font-semibold">Total Leaves</span>
                <div className="text-gray-900 dark:text-white font-medium text-lg">{selectedLeaveBalance.total_leave_balance}</div>
              </div>

              <div className="bg-gray-100 dark:bg-black p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
                <span className="text-gray-500 dark:text-gray-400 font-sans font-semibold">Leaves Taken</span>
                <div className="text-gray-900 dark:text-white font-medium text-lg">{selectedLeaveBalance.leave_taken}</div>
              </div>

              <div className="bg-gray-100 dark:bg-black p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
                <span className="text-gray-500 dark:text-gray-400 font-sans font-semibold">Remaining Leaves</span>
                <div className="text-gray-900 dark:text-white font-medium text-lg">{selectedLeaveBalance.leave_balance_remaining}</div>
              </div>

              <div className="bg-gray-100 dark:bg-black p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
                <span className="text-gray-500 dark:text-gray-400 font-sans font-semibold">Carry-forward Balance</span>
                <div className="text-gray-900 dark:text-white font-medium text-lg">{selectedLeaveBalance.carry_forward_balance}</div>
              </div>

              <div className="bg-gray-100 dark:bg-black p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
                <span className="text-gray-500 dark:text-gray-400 font-sans font-semibold">On Notice Period</span>
                <div className="text-gray-900 dark:text-white font-medium text-lg">{selectedLeaveBalance.is_on_notice === 1 ? "Yes" : "No"}</div>
              </div>
            </div>
            
            <div className="flex justify-end mt-6">
              <button
                onClick={closeViewModal}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal for Bulk create */}
      <Modal
        isOpen={isBulkModalOpen}
        onRequestClose={() => setIsBulkModalOpen(false)}
        title="Add Bulk Leave Balance"
      >
        <div className="p-4 space-y-4 font-poppins text-center">
          <p className="text-gray-600 dark:text-gray-400">
            Are you sure you want to create leave balances for all eligible employees in bulk?
          </p>
          
          {bulkMessage && (
            <div className={`p-3 rounded-md text-sm ${bulkMessage.toLowerCase().includes("success") ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
              {bulkMessage}
            </div>
          )}

          <div className="flex justify-center gap-4 mt-6">
            <button
              onClick={() => setIsBulkModalOpen(false)}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-800 transition-colors"
              disabled={bulkLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleBulkLeaveBalance}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
              disabled={bulkLoading}
            >
              {bulkLoading && (
                <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              {bulkLoading ? "Processing..." : "Yes, Proceed"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
});

export default LeaveBalanceMasters;
